import { useCallback, useEffect, useState } from "react";
import {
  MousePointerClick, Plus, Edit3, Trash2, Save, X, Loader2, ToggleLeft, ToggleRight,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";

/**
 * AdminCrmBotoes — gestão dinâmica dos chips de acção rápida do CRM.
 *
 * Estes botões aparecem hoje no modal "Evoluir Lead" (`EvolvePanel`) para
 * permitir ao consultor marcar rapidamente situações comuns (ex: "Enviei
 * email", "Reunião agendada"). Alterações aqui reflectem-se imediatamente
 * no CRM na próxima abertura do modal — não precisa de deploy.
 */
export default function AdminCrmBotoes() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        adminApi.crmBotoesList(),
        adminApi.crmBotoesStats(days),
      ]);
      setRows(r.botoes || []);
      setStats(s.stats || {});
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const groups = rows.reduce((acc, b) => {
    (acc[b.Contexto] = acc[b.Contexto] || []).push(b);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl" data-testid="admin-crm-botoes">
      <div className="mb-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#A8843C] grid place-items-center text-white shrink-0">
          <MousePointerClick size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl text-[#1B1B1B]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>
            Botões do CRM
          </h1>
          <p className="text-sm text-[#6E6754]">
            Chips de acção rápida disponíveis no modal &quot;Evoluir Lead&quot;.
            Alterações reflectem-se imediatamente no CRM.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          data-testid="crm-botao-new-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#A8843C] text-white text-sm rounded hover:bg-[#8B6E32]"
        >
          <Plus size={14} /> Novo botão
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 text-xs">
        <span className="text-[#6E6754]">Período de utilização:</span>
        {[7, 30, 90, 365].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setDays(n)}
            data-testid={`crm-botoes-days-${n}`}
            className={
              `px-2 py-1 rounded border transition ` +
              (days === n
                ? 'bg-[#A8843C] text-white border-[#A8843C]'
                : 'bg-white text-[#6E6754] border-[#E6E1D6] hover:border-[#A8843C]')
            }
          >
            {n === 365 ? '1 ano' : `${n}d`}
          </button>
        ))}
      </div>

      {loading && (
        <div className="p-6 text-sm text-[#978D78] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> A carregar…
        </div>
      )}

      {!loading && showNew && (
        <BotaoForm onCancel={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
      )}

      {!loading && Object.keys(groups).sort().map((ctx) => (
        <section key={ctx} className="mb-6">
          <h2 className="text-[11px] uppercase tracking-widest font-bold text-[#7A5B14] mb-2">
            Contexto: {ctx}
          </h2>
          <div className="bg-white border border-[#E6E1D6] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#FAF8F2] text-[10px] uppercase tracking-widest text-[#978D78]">
                <tr>
                  <th className="text-left px-3 py-2 w-14">ID</th>
                  <th className="text-left px-3 py-2 w-40">Código</th>
                  <th className="text-left px-3 py-2">Label</th>
                  <th className="text-right px-3 py-2 w-14">Ordem</th>
                  <th className="text-right px-3 py-2 w-32">Utilizações ({days}d)</th>
                  <th className="text-center px-3 py-2 w-20">Activo</th>
                  <th className="text-right px-3 py-2 w-24">Acções</th>
                </tr>
              </thead>
              <tbody>
                {groups[ctx].map((b) => (
                  <BotaoRow key={b.ID_Botao} b={b} stat={stats[b.ID_Botao]} onChange={load} />
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </section>
      ))}

      {!loading && rows.length === 0 && !showNew && (
        <div className="p-6 bg-white border border-[#E6E1D6] rounded text-sm text-[#978D78]">
          Ainda não existe nenhum botão configurado.
        </div>
      )}
    </div>
  );
}


function BotaoRow({ b, stat, onChange }) {
  const [editing, setEditing] = useState(false);
  const total = stat?.total || 0;
  const t = stat?.tentativas || 0;
  const e = stat?.evolucoes || 0;
  const toggleActivo = async () => {
    try {
      await adminApi.crmBotaoUpdate(b.ID_Botao, { Activo: !b.Activo });
      toast.success(b.Activo ? "Desactivado" : "Activado");
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro"); }
  };
  const del = async () => {
    if (!confirm(`Desactivar o botão "${b.Label}"?`)) return;
    try {
      await adminApi.crmBotaoDelete(b.ID_Botao);
      toast.success("Desactivado.");
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={7} className="p-2 bg-[#FAF8F2]">
          <BotaoForm b={b} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChange?.(); }} />
        </td>
      </tr>
    );
  }

  return (
    <tr data-testid={`crm-botao-row-${b.ID_Botao}`} className={`border-t border-[#F0EBDF] ${!b.Activo ? "opacity-50" : ""}`}>
      <td className="px-3 py-2 font-mono text-[11px] text-[#978D78]">{b.ID_Botao}</td>
      <td className="px-3 py-2 font-mono text-[11px]">{b.Codigo}</td>
      <td className="px-3 py-2 font-medium text-[#1B1B1B]">{b.Label}</td>
      <td className="px-3 py-2 text-right tabular-nums">{b.Ordem}</td>
      <td className="px-3 py-2 text-right tabular-nums" data-testid={`crm-botao-usage-${b.ID_Botao}`}>
        <div
          className={`inline-flex items-baseline gap-1 ${total === 0 ? 'text-[#B8B0A0]' : 'text-[#1B1B1B]'}`}
          title={`${t} em "Não atendeu" + ${e} em "Evoluir Lead"`}
        >
          <span className="font-semibold">{total}</span>
          {total > 0 && (
            <span className="text-[10px] text-[#978D78]">({t}+{e})</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-center">
        <button
          type="button"
          onClick={toggleActivo}
          data-testid={`crm-botao-toggle-${b.ID_Botao}`}
          className={b.Activo ? "text-green-600" : "text-[#B8B0A0]"}
          title={b.Activo ? "Desactivar" : "Activar"}
        >
          {b.Activo ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
        </button>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-1">
          <button type="button" onClick={() => setEditing(true)} data-testid={`crm-botao-edit-${b.ID_Botao}`}
            className="text-[#6E6754] hover:text-[#A8843C] p-1"><Edit3 size={13} /></button>
          <button type="button" onClick={del} data-testid={`crm-botao-delete-${b.ID_Botao}`}
            className="text-[#B8B0A0] hover:text-red-600 p-1"><Trash2 size={13} /></button>
        </div>
      </td>
    </tr>
  );
}


function BotaoForm({ b = null, onCancel, onSaved }) {
  const isEdit = !!b;
  const [codigo, setCodigo] = useState(b?.Codigo || "");
  const [label, setLabel] = useState(b?.Label || "");
  const [contexto, setContexto] = useState(b?.Contexto || "lead_evolve");
  const [ordem, setOrdem] = useState(b?.Ordem ?? 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim()) { toast.error("Label é obrigatório."); return; }
    if (!isEdit && !codigo.trim()) { toast.error("Código é obrigatório."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.crmBotaoUpdate(b.ID_Botao, {
          Label: label, Ordem: parseInt(ordem, 10) || 0,
        });
      } else {
        await adminApi.crmBotaoCreate({
          Codigo: codigo.toLowerCase(),
          Label: label,
          Contexto: contexto,
          Ordem: parseInt(ordem, 10) || 0,
        });
      }
      toast.success(isEdit ? "Actualizado." : "Criado.");
      onSaved?.();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={isEdit ? `crm-botao-form-edit-${b.ID_Botao}` : "crm-botao-form-new"}
      className="bg-white border border-[#A8843C] rounded-lg p-3">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">Código</label>
          <input type="text" value={codigo} disabled={isEdit}
            onChange={(e) => setCodigo(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            placeholder="ex: reagendou"
            data-testid="crm-botao-form-codigo"
            className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs font-mono disabled:bg-[#F5F2EB]" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">Label</label>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Reagendou reunião"
            data-testid="crm-botao-form-label"
            className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">Contexto</label>
          <select value={contexto} onChange={(e) => setContexto(e.target.value)} disabled={isEdit}
            data-testid="crm-botao-form-contexto"
            className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs disabled:bg-[#F5F2EB]">
            <option value="lead_evolve">lead_evolve</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">Ordem</label>
          <input type="number" min={0} value={ordem} onChange={(e) => setOrdem(e.target.value)}
            data-testid="crm-botao-form-ordem"
            className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs tabular-nums" />
        </div>
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="crm-botao-form-cancel"
          className="inline-flex items-center gap-1 px-2 py-1 border border-[#E6E1D6] rounded text-xs text-[#6E6754]">
          <X size={11} /> Cancelar
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid="crm-botao-form-save"
          className="inline-flex items-center gap-1 px-3 py-1 bg-[#A8843C] text-white text-xs rounded hover:bg-[#8B6E32] disabled:opacity-60">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          Guardar
        </button>
      </div>
    </div>
  );
}
